/**
 * XBRL QName constants for MBRS taxonomies.
 *
 * A "qname" (qualified name) is an XBRL element identifier combining a
 * namespace prefix with a local name — e.g. `ssmt-dei:StatusOfCompany`.
 * Centralising qnames here:
 *   - removes typo-prone string literals scattered across the codebase,
 *   - gives IDE autocomplete and find-references over every field,
 *   - lets us evolve the underlying taxonomy in one place.
 *
 * Source of truth for the full taxonomy lives in:
 *   - `src/assets/unified_taxonomy*.json`
 *   - `src/assets/forms/*.form.json`
 *
 * If a qname you need is missing here, add it — do not inline a new
 * string literal. The constant key mirrors the local part of the qname
 * (PascalCase) so `DEI.StatusOfCompany` is easy to cross-reference
 * against the taxonomy JSON.
 *
 * Migration is phased (see CLAUDE.md "boy scout, not bulldozer"):
 * entries are added as files are migrated off hardcoded literals.
 */

/**
 * SSMT — Suruhanjaya Syarikat Malaysia (Companies Commission of
 * Malaysia). The base taxonomy namespace for cross-statement concepts
 * that are not scoped to the DEI header block.
 */
export const SSMT = {
  AccruedExpensesLiquidity: 'ssmt:AccruedExpensesLiquidity',
  AccruedIncomeLiquidity: 'ssmt:AccruedIncomeLiquidity',
  AcquisitionOfSubsidiaries: 'ssmt:AcquisitionOfSubsidiaries',
  AllowanceForDoubtfulDebtsLiquidity: 'ssmt:AllowanceForDoubtfulDebtsLiquidity',
  BankOverdraftsSubClassificationLiquidity:
    'ssmt:BankOverdraftsSubClassificationLiquidity',
  BorrowingsSubClassificationLiquidity:
    'ssmt:BorrowingsSubClassificationLiquidity',
  BrandNamesLiquidity: 'ssmt:BrandNamesLiquidity',
  BusinessOneMember: 'ssmt:BusinessOneMember',
  CapitalWorkInProgressLiquidity: 'ssmt:CapitalWorkInProgressLiquidity',
  CashAndBankBalances: 'ssmt:CashAndBankBalances',
  CashAndCashEquivalentsAtBeginningOfYear:
    'ssmt:CashAndCashEquivalentsAtBeginningOfYear',
  CashAndCashEquivalentsAtEndOfYear: 'ssmt:CashAndCashEquivalentsAtEndOfYear',
  CashGeneratedFromOperations: 'ssmt:CashGeneratedFromOperations',
  CashPaidToSuppliersAndEmployees: 'ssmt:CashPaidToSuppliersAndEmployees',
  CashReceiptsFromCustomers: 'ssmt:CashReceiptsFromCustomers',
  ComputerSoftwareLiquidity: 'ssmt:ComputerSoftwareLiquidity',
  ConsumablesAndSparesLiquidity: 'ssmt:ConsumablesAndSparesLiquidity',
  DateOfSigningAuditorsReport: 'ssmt:DateOfSigningAuditorsReport',
  DateOfSigningDirectorsReport: 'ssmt:DateOfSigningDirectorsReport',
  DepositsLiquidity: 'ssmt:DepositsLiquidity',
  DepositsReceivedLiquidity: 'ssmt:DepositsReceivedLiquidity',
  DescriptionOfBusiness: 'ssmt:DescriptionOfBusiness',
  DisclosureOfKeyAuditMatters: 'ssmt:DisclosureOfKeyAuditMatters',
  DisclosureOfMaterialUncertaintyRelatedToGoingConcern:
    'ssmt:DisclosureOfMaterialUncertaintyRelatedToGoingConcern',
  DisclosureOfOtherBusinessReviewApplied:
    'ssmt:DisclosureOfOtherBusinessReviewApplied',
  DisclosureOfWhetherAuditorsReportContainsKeyAuditMatters:
    'ssmt:DisclosureOfWhetherAuditorsReportContainsKeyAuditMatters',
  DisclosureOfWhetherAuditorsReportContainsMaterialUncertaintyRelatedToGoingConcern:
    'ssmt:DisclosureOfWhetherAuditorsReportContainsMaterialUncertaintyRelatedToGoingConcern',
  DividendsPaid: 'ssmt:DividendsPaid',
  DividendsPaidToNonControllingInterests:
    'ssmt:DividendsPaidToNonControllingInterests',
  DividendsReceived: 'ssmt:DividendsReceived',
  EffectOfExchangeRateChanges: 'ssmt:EffectOfExchangeRateChanges',
  FinishedGoodsLiquidity: 'ssmt:FinishedGoodsLiquidity',
  ForeignCurrencyTranslationReserveLiquidity:
    'ssmt:ForeignCurrencyTranslationReserveLiquidity',
  FurnitureFixturesAndFittingsLiquidity:
    'ssmt:FurnitureFixturesAndFittingsLiquidity',
  GoodwillLiquidity: 'ssmt:GoodwillLiquidity',
  IdentificationNumberOfFifthDirectorWhoSignedDirectorsReport:
    'ssmt:IdentificationNumberOfFifthDirectorWhoSignedDirectorsReport',
  IdentificationNumberOfFirstDirectorWhoSignedDirectorsReport:
    'ssmt:IdentificationNumberOfFirstDirectorWhoSignedDirectorsReport',
  IdentificationNumberOfFourthDirectorWhoSignedDirectorsReport:
    'ssmt:IdentificationNumberOfFourthDirectorWhoSignedDirectorsReport',
  IdentificationNumberOfSecondDirectorWhoSignedDirectorsReport:
    'ssmt:IdentificationNumberOfSecondDirectorWhoSignedDirectorsReport',
  IdentificationNumberOfThirdDirectorWhoSignedDirectorsReport:
    'ssmt:IdentificationNumberOfThirdDirectorWhoSignedDirectorsReport',
  IncomeTaxesPaid: 'ssmt:IncomeTaxesPaid',
  IntangibleAssetsSubClassificationLiquidity:
    'ssmt:IntangibleAssetsSubClassificationLiquidity',
  InterestPaid: 'ssmt:InterestPaid',
  InterestReceived: 'ssmt:InterestReceived',
  InventoriesSubClassificationLiquidity:
    'ssmt:InventoriesSubClassificationLiquidity',
  LandAndBuildingsLiquidity: 'ssmt:LandAndBuildingsLiquidity',
  MSICCode: 'ssmt:MSICCode',
  MotorVehiclesLiquidity: 'ssmt:MotorVehiclesLiquidity',
  NetCashFromFinancingActivities: 'ssmt:NetCashFromFinancingActivities',
  NetCashFromOperatingActivities: 'ssmt:NetCashFromOperatingActivities',
  NetCashUsedInInvestingActivities: 'ssmt:NetCashUsedInInvestingActivities',
  NetIncreaseDecreaseInCashAndCashEquivalents:
    'ssmt:NetIncreaseDecreaseInCashAndCashEquivalents',
  NumberOfDirectorsSigningDirectorsReport:
    'ssmt:NumberOfDirectorsSigningDirectorsReport',
  NumberOfDirectorsSigningStatementByDirectors:
    'ssmt:NumberOfDirectorsSigningStatementByDirectors',
  OfficeEquipmentLiquidity: 'ssmt:OfficeEquipmentLiquidity',
  OrdinarySharesLiquidity: 'ssmt:OrdinarySharesLiquidity',
  Others: 'ssmt:Others',
  OtherBorrowingsLiquidity: 'ssmt:OtherBorrowingsLiquidity',
  OtherCapitalReservesLiquidity: 'ssmt:OtherCapitalReservesLiquidity',
  OtherIntangibleAssetsLiquidity: 'ssmt:OtherIntangibleAssetsLiquidity',
  OtherPayablesSubClassificationLiquidity:
    'ssmt:OtherPayablesSubClassificationLiquidity',
  OtherReceivablesSubClassificationLiquidity:
    'ssmt:OtherReceivablesSubClassificationLiquidity',
  OtherReservesSubClassificationLiquidity:
    'ssmt:OtherReservesSubClassificationLiquidity',
  PaymentOfLeaseLiabilities: 'ssmt:PaymentOfLeaseLiabilities',
  PlantAndMachineryLiquidity: 'ssmt:PlantAndMachineryLiquidity',
  PreferenceSharesLiquidity: 'ssmt:PreferenceSharesLiquidity',
  PrepaymentsLiquidity: 'ssmt:PrepaymentsLiquidity',
  ProceedsFromBorrowings: 'ssmt:ProceedsFromBorrowings',
  ProceedsFromDisposalOfInvestments: 'ssmt:ProceedsFromDisposalOfInvestments',
  ProceedsFromDisposalOfPropertyPlantAndEquipment:
    'ssmt:ProceedsFromDisposalOfPropertyPlantAndEquipment',
  ProceedsFromIssuanceOfShareCapital:
    'ssmt:ProceedsFromIssuanceOfShareCapital',
  PropertyPlantAndEquipmentSubClassificationLiquidity:
    'ssmt:PropertyPlantAndEquipmentSubClassificationLiquidity',
  PurchaseOfIntangibleAssets: 'ssmt:PurchaseOfIntangibleAssets',
  PurchaseOfInvestments: 'ssmt:PurchaseOfInvestments',
  PurchaseOfPropertyPlantAndEquipment:
    'ssmt:PurchaseOfPropertyPlantAndEquipment',
  RawMaterialsLiquidity: 'ssmt:RawMaterialsLiquidity',
  RepaymentOfBorrowings: 'ssmt:RepaymentOfBorrowings',
  RevaluationSurplusLiquidity: 'ssmt:RevaluationSurplusLiquidity',
  RevolvingCreditLiquidity: 'ssmt:RevolvingCreditLiquidity',
  ShareCapitalSubClassificationLiquidity:
    'ssmt:ShareCapitalSubClassificationLiquidity',
  SundryPayablesLiquidity: 'ssmt:SundryPayablesLiquidity',
  TermLoansLiquidity: 'ssmt:TermLoansLiquidity',
  TotalAssetsLiquidity: 'ssmt:TotalAssetsLiquidity',
  TotalEquityAndLiabilitiesLiquidity: 'ssmt:TotalEquityAndLiabilitiesLiquidity',
  TotalEquityLiquidity: 'ssmt:TotalEquityLiquidity',
  TotalLiabilitiesLiquidity: 'ssmt:TotalLiabilitiesLiquidity',
  TradePayablesDomesticLiquidity: 'ssmt:TradePayablesDomesticLiquidity',
  TradePayablesForeignLiquidity: 'ssmt:TradePayablesForeignLiquidity',
  TradePayablesSubClassificationLiquidity:
    'ssmt:TradePayablesSubClassificationLiquidity',
  TradeReceivablesGrossAmountLiquidity:
    'ssmt:TradeReceivablesGrossAmountLiquidity',
  TradeReceivablesNetAmountLiquidity: 'ssmt:TradeReceivablesNetAmountLiquidity',
  TypeOfIdentificationOfFifthDirectorWhoSignedDirectorsReport:
    'ssmt:TypeOfIdentificationOfFifthDirectorWhoSignedDirectorsReport',
  TypeOfIdentificationOfFirstDirectorWhoSignedDirectorsReport:
    'ssmt:TypeOfIdentificationOfFirstDirectorWhoSignedDirectorsReport',
  TypeOfIdentificationOfFourthDirectorWhoSignedDirectorsReport:
    'ssmt:TypeOfIdentificationOfFourthDirectorWhoSignedDirectorsReport',
  TypeOfIdentificationOfSecondDirectorWhoSignedDirectorsReport:
    'ssmt:TypeOfIdentificationOfSecondDirectorWhoSignedDirectorsReport',
  TypeOfIdentificationOfThirdDirectorWhoSignedDirectorsReport:
    'ssmt:TypeOfIdentificationOfThirdDirectorWhoSignedDirectorsReport',
  WorkInProgressLiquidity: 'ssmt:WorkInProgressLiquidity',
} as const;

/**
 * SSMT_SCOPE — Scope-of-Filing sheet. Top-level discriminators that
 * control which sheets and which columns are presented in the filing:
 * whether the statements are consolidated, whether comparative (prior
 * period) information is presented, etc. Fields sit under the
 * `ssmt-scope:` prefix.
 */
export const SSMT_SCOPE = {
  DisclosureOfComparativeInformation:
    'ssmt-scope:DisclosureOfComparativeInformation',
  DisclosureOfWhetherFinancialStatementsAreConsolidated:
    'ssmt-scope:DisclosureOfWhetherFinancialStatementsAreConsolidated',
} as const;

/**
 * DEI — Document and Entity Information.
 *
 * The header block present on every MBRS filing: filing period, company
 * status, accounting-standard basis, audit status, presentation
 * currency, rounding, submission type, etc. Fields in this namespace
 * sit under the `ssmt-dei:` prefix.
 */
export const DEI = {
  ApplicationOfSubmission: 'ssmt-dei:ApplicationOfSubmission',
  BasisOfAccountingStandardsAppliedToPrepareFinancialStatements:
    'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements',
  CompanyCurrentFinancialYearStartDate:
    'ssmt-dei:CompanyCurrentFinancialYearStartDate',
  CompanyCurrentFinancialYearEndDate:
    'ssmt-dei:CompanyCurrentFinancialYearEndDate',
  CompanyPreviousFinancialYearStartDate:
    'ssmt-dei:CompanyPreviousFinancialYearStartDate',
  CompanyPreviousFinancialYearEndDate:
    'ssmt-dei:CompanyPreviousFinancialYearEndDate',
  CompanyRegistrationNumber: 'ssmt-dei:CompanyRegistrationNumber',
  CourtOrderReferenceNumber: 'ssmt-dei:CourtOrderReferenceNumber',
  DescriptionOfFunctionalCurrency:
    'ssmt-dei:DescriptionOfFunctionalCurrency',
  DescriptionOfPresentationCurrency:
    'ssmt-dei:DescriptionOfPresentationCurrency',
  DisclosureOfFinancialStatementsAuditStatus:
    'ssmt-dei:DisclosureOfFinancialStatementsAuditStatus',
  DisclosureOfFinancialStatementsPreparationForCurrentSubmission:
    'ssmt-dei:DisclosureOfFinancialStatementsPreparationForCurrentSubmission',
  LevelOfRoundingUsedInFinancialStatements:
    'ssmt-dei:LevelOfRoundingUsedInFinancialStatements',
  NameAndVersionOfSoftwareUsedToGenerateXBRLFile:
    'ssmt-dei:NameAndVersionOfSoftwareUsedToGenerateXBRLFile',
  NameOfReportingEntity: 'ssmt-dei:NameOfReportingEntity',
  NewCompanyRegistrationNumber: 'ssmt-dei:NewCompanyRegistrationNumber',
  OriginOfCompany: 'ssmt-dei:OriginOfCompany',
  StatusOfCarryingOnBusinessDuringFinancialYear:
    'ssmt-dei:StatusOfCarryingOnBusinessDuringFinancialYear',
  StatusOfCompany: 'ssmt-dei:StatusOfCompany',
  StatutoryDeclarationForRectification:
    'ssmt-dei:StatutoryDeclarationForRectification',
  TaxonomyVersion: 'ssmt-dei:TaxonomyVersion',
  TypeOfCompany: 'ssmt-dei:TypeOfCompany',
  TypeOfSubmission: 'ssmt-dei:TypeOfSubmission',
} as const;

/**
 * DEI_EE_MFRS — DEI Entity Extension for MFRS.
 *
 * DEI-scoped fields that only apply when the filer has declared the
 * MFRS accounting-standard basis. Fields sit under the
 * `ssmt-dei-ee-mfrs:` prefix.
 *
 * Abbreviations in this prefix:
 *   - DEI = Document and Entity Information (header block — see DEI above).
 *   - EE  = Entity Extension (taxonomy extension scoped to a specific
 *           reporting-standard variant).
 *   - MFRS = Malaysian Financial Reporting Standards (the IFRS-aligned
 *           standard applied by public-interest entities in Malaysia).
 */
export const DEI_EE_MFRS = {
  NatureOfFinancialStatements: 'ssmt-dei-ee-mfrs:NatureOfFinancialStatements',
} as const;
